import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserGroupIcon, BriefcaseIcon, CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';
import LeadAssignmentService from '../services/leadAssignmentService';
import { employeeService } from '../services/employeeService';
import { departmentService } from '../services/departmentService';
import { useAuth } from '../context/AuthContext';

const EnhancedLeadAssignmentModal = ({ 
  isOpen, 
  onClose, 
  selectedLeads = [], 
  onAssignmentComplete 
}) => {
  const { user } = useAuth();
  
  // State for company-wise organization
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedEmployees, setSelectedEmployees] = useState([]); // Multiple selection
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('department');

  useEffect(() => {
    if (isOpen) {
      loadAllData();
      // Pre-select currently assigned employees
      if (selectedLeads.length === 1) {
        const lead = selectedLeads[0];
        if (lead.assignedEmployees && lead.assignedEmployees.length > 0) {
          setSelectedEmployees(lead.assignedEmployees.map(emp => emp.employeeId || emp._id));
        } else if (lead.assignedTo) {
          const assignedIds = Array.isArray(lead.assignedTo) ? lead.assignedTo : [lead.assignedTo];
          setSelectedEmployees(assignedIds);
        }
      }
    }
  }, [isOpen, selectedLeads]);

  // Load all data
  const loadAllData = async () => {
    setLoadingData(true);
    setError('');
    
    try {
      const employeesByDeptRes = await employeeService.getEmployeesByDepartments();
      
      if (employeesByDeptRes.success) {
        setEmployees(employeesByDeptRes.data || []);
        setDepartments(employeesByDeptRes.departments || []);
      } else {
        const employeesRes = await employeeService.getAllEmployees();
        if (employeesRes.success) {
          setEmployees(employeesRes.data || []);
        }
        
        const departmentsRes = await departmentService.getAllDepartments();
        if (departmentsRes.success) {
          setDepartments(departmentsRes.data || []);
        }
      }
      
      if (user && user.companyId) {
        setCompanies([{
          _id: user.companyId,
          businessName: user.businessName || 'Your Company'
        }]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load employee data');
    }
    
    setLoadingData(false);
  };

  // Toggle employee selection
  const toggleEmployeeSelection = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  // Check if employee is selected
  const isEmployeeSelected = (employeeId) => {
    return selectedEmployees.includes(employeeId);
  };

  // Get currently assigned employees for display
  const getCurrentlyAssigned = () => {
    if (selectedLeads.length === 1) {
      const lead = selectedLeads[0];
      return lead.assignedEmployees || [];
    }
    return [];
  };

  const handleAssignment = async () => {
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    if (selectedLeads.length === 0) {
      setError('No leads selected for assignment');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get employee details for selected employees
      const selectedEmployeeDetails = employees
        .filter(emp => selectedEmployees.includes(emp._id))
        .map(emp => ({
          employeeId: emp._id,
          name: emp.teamMemberName || emp.name,
          email: emp.teamMemberEmail || emp.email || '',
          phone: emp.mobileNumber || emp.phone || ''
        }));

      // Check if this is a reassignment
      const isReassignment = selectedLeads.length === 1 && currentlyAssigned.length > 0;
      
      // Calculate added and removed employees
      let addedEmployees = [];
      let removedEmployees = [];
      let keptEmployees = [];
      
      if (isReassignment) {
        const currentIds = currentlyAssigned.map(e => e.employeeId || e._id);
        
        // Find removed employees (were assigned but not selected now)
        removedEmployees = currentlyAssigned.filter(e => 
          !selectedEmployees.includes(e.employeeId || e._id)
        );
        
        // Find kept employees (were assigned and still selected)
        keptEmployees = currentlyAssigned.filter(e => 
          selectedEmployees.includes(e.employeeId || e._id)
        );
        
        // Find added employees (newly selected, not previously assigned)
        addedEmployees = selectedEmployeeDetails.filter(e => 
          !currentIds.includes(e.employeeId)
        );
      }
      
      // Build assignment notes
      let assignmentNotes = notes.trim();
      if (!assignmentNotes && isReassignment) {
        const parts = [];
        if (removedEmployees.length > 0) {
          parts.push(`Removed: ${removedEmployees.map(e => e.name).join(', ')}`);
        }
        if (addedEmployees.length > 0) {
          parts.push(`Added: ${addedEmployees.map(e => e.name).join(', ')}`);
        }
        if (keptEmployees.length > 0) {
          parts.push(`Kept: ${keptEmployees.map(e => e.name).join(', ')}`);
        }
        assignmentNotes = parts.join(' | ');
      } else if (!assignmentNotes) {
        assignmentNotes = `Assignment of ${selectedLeads.length} leads to ${selectedEmployees.length} employees`;
      }
      
      const assignmentData = {
        leadIds: selectedLeads.map(lead => lead._id || lead.id),
        assignedTo: selectedEmployees,
        assignedEmployees: selectedEmployeeDetails,
        assignmentNotes,
        priority,
        isReassignment,
        previousAssignment: isReassignment ? currentlyAssigned : null,
        addedEmployees,
        removedEmployees,
        keptEmployees
      };

      const result = await LeadAssignmentService.bulkAssignLeads(assignmentData);

      if (result.success) {
        let message = '';
        if (isReassignment) {
          const messageParts = [];
          if (addedEmployees.length > 0) {
            messageParts.push(`Added: ${addedEmployees.map(e => e.name).join(', ')}`);
          }
          if (removedEmployees.length > 0) {
            messageParts.push(`Removed: ${removedEmployees.map(e => e.name).join(', ')}`);
          }
          if (keptEmployees.length > 0) {
            messageParts.push(`Kept: ${keptEmployees.map(e => e.name).join(', ')}`);
          }
          message = `Lead reassigned - ${messageParts.join(' • ')}`;
        } else {
          message = `Successfully assigned ${selectedLeads.length} leads to ${selectedEmployees.length} employees`;
        }
          
        onAssignmentComplete?.({
          success: true,
          message,
          assignedCount: selectedLeads.length,
          isReassignment,
          addedCount: addedEmployees.length,
          removedCount: removedEmployees.length
        });
        handleClose();
      } else {
        setError(result.message || 'Assignment failed');
      }
    } catch (error) {
      console.error('Assignment error:', error);
      setError('Failed to assign leads');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedDepartment('');
    setSelectedEmployees([]);
    setPriority('medium');
    setNotes('');
    setError('');
    setViewMode('department');
    onClose();
  };

  // Get filtered employees based on view mode and department
  const getFilteredEmployees = () => {
    let filtered = employees;
    
    if (viewMode === 'department' && selectedDepartment) {
      filtered = filtered.filter(emp => {
        if (!emp.department) return false;
        
        if (Array.isArray(emp.department)) {
          return emp.department.some(dept => {
            const deptId = typeof dept === 'object' ? dept._id : dept;
            return deptId === selectedDepartment;
          });
        }
        
        const empDeptId = typeof emp.department === 'object' ? emp.department._id : emp.department;
        return empDeptId === selectedDepartment;
      });
    }
    
    return filtered;
  };

  if (!isOpen) return null;

  const currentlyAssigned = getCurrentlyAssigned();
  const filteredEmployees = getFilteredEmployees();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden relative z-[10000]">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <UserGroupIcon className="h-6 w-6 text-white" />
              <h2 className="text-xl font-semibold text-white">
                Assign Leads to Sales Team
              </h2>
            </div>
            <button onClick={handleClose} className="text-white/80 hover:text-white">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Selected Leads Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">
              Selected Leads ({selectedLeads.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {selectedLeads.slice(0, 5).map((lead, index) => (
                <span key={lead._id || index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {lead.name}
                </span>
              ))}
              {selectedLeads.length > 5 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                  +{selectedLeads.length - 5} more
                </span>
              )}
            </div>
          </div>

          {/* Currently Assigned - Show only for single lead */}
          {selectedLeads.length === 1 && currentlyAssigned.length > 0 && (
            <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
                <UserGroupIcon className="h-5 w-5" />
                Currently Assigned To ({currentlyAssigned.length})
              </h3>
              <div className="flex flex-wrap gap-2 mb-3">
                {currentlyAssigned.map((emp, idx) => {
                  const empId = emp.employeeId || emp._id;
                  const isStillSelected = selectedEmployees.includes(empId);
                  
                  return (
                    <div key={idx} className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium border-2 shadow-sm transition-all ${
                      isStillSelected 
                        ? 'bg-white border-amber-400' 
                        : 'bg-gray-100 border-gray-300 opacity-60'
                    }`}>
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        isStillSelected ? 'bg-amber-600' : 'bg-gray-400'
                      }`}>
                        {(emp.name || 'E').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex flex-col">
                        <span className={`font-semibold ${isStillSelected ? 'text-gray-900' : 'text-gray-500 line-through'}`}>
                          {emp.name}
                        </span>
                        {emp.email && <span className="text-xs text-gray-600">{emp.email}</span>}
                      </div>
                      {isStillSelected ? (
                        <button
                          onClick={() => toggleEmployeeSelection(empId)}
                          className="ml-2 p-1 hover:bg-red-100 rounded-full transition-colors group"
                          title="Remove from assignment"
                        >
                          <XCircleIcon className="h-5 w-5 text-red-500 group-hover:text-red-700" />
                        </button>
                      ) : (
                        <button
                          onClick={() => toggleEmployeeSelection(empId)}
                          className="ml-2 p-1 hover:bg-green-100 rounded-full transition-colors group"
                          title="Keep in assignment"
                        >
                          <CheckIcon className="h-5 w-5 text-green-500 group-hover:text-green-700" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="bg-amber-100 border border-amber-300 rounded-md p-3">
                <p className="text-sm text-amber-900 font-medium mb-1">
                  🔄 Reassignment Mode
                </p>
                <p className="text-xs text-amber-800">
                  • Click ❌ to remove current employees from assignment<br/>
                  • Click ✓ to keep them in assignment<br/>
                  • Select new employees below to add them to the assignment
                </p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* View Mode Toggle */}
          <div className="flex space-x-3">
            <button
              onClick={() => { setViewMode('department'); setSelectedDepartment(''); }}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                viewMode === 'department'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400'
              }`}
            >
              <BriefcaseIcon className="h-4 w-4 inline mr-2" />
              Department Wise
            </button>
            <button
              onClick={() => { setViewMode('all'); setSelectedDepartment(''); }}
              className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                viewMode === 'all'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-400'
              }`}
            >
              <UserGroupIcon className="h-4 w-4 inline mr-2" />
              All Employees
            </button>
          </div>

          {loadingData ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading...</span>
            </div>
          ) : (
            <>
              {/* Department Selection */}
              {viewMode === 'department' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🏢 Select Department
                  </label>
                  <select
                    value={selectedDepartment}
                    onChange={(e) => setSelectedDepartment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose a department...</option>
                    {departments.map((dept) => (
                      <option key={dept._id} value={dept._id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Employee Multi-Select Grid */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">
                    👥 Select Employees (Multiple)
                    {selectedEmployees.length > 0 && (
                      <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 rounded-full text-xs">
                        {selectedEmployees.length} selected
                      </span>
                    )}
                  </label>
                  {selectedEmployees.length > 0 && (
                    <button
                      onClick={() => setSelectedEmployees([])}
                      className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                    >
                      <XCircleIcon className="h-4 w-4" />
                      Clear All
                    </button>
                  )}
                </div>

                {(viewMode === 'department' && !selectedDepartment) ? (
                  <p className="text-sm text-amber-600 py-4 text-center bg-amber-50 rounded-lg">
                    ⚠️ Please select a department first to see employees
                  </p>
                ) : filteredEmployees.length === 0 ? (
                  <p className="text-sm text-gray-500 py-4 text-center bg-gray-50 rounded-lg">
                    No employees found
                  </p>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                    {filteredEmployees.map((employee) => {
                      const isSelected = isEmployeeSelected(employee._id);
                      const isCurrentlyAssigned = currentlyAssigned.some(
                        emp => emp.employeeId === employee._id || emp._id === employee._id
                      );
                      
                      return (
                        <div
                          key={employee._id}
                          onClick={() => toggleEmployeeSelection(employee._id)}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border-2 ${
                            isSelected
                              ? 'bg-blue-50 border-blue-500 shadow-sm'
                              : 'bg-white border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                          }`}
                        >
                          {/* Checkbox */}
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}>
                            {isSelected && <CheckIcon className="h-3 w-3 text-white" />}
                          </div>
                          
                          {/* Avatar */}
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${
                            isSelected ? 'bg-blue-600' : 'bg-gray-400'
                          }`}>
                            {(employee.teamMemberName || employee.name || 'E').charAt(0).toUpperCase()}
                          </div>
                          
                          {/* Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900 text-sm truncate">
                                {employee.teamMemberName || employee.name}
                              </span>
                              {isCurrentlyAssigned && (
                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded">
                                  Current
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {employee.teamMemberEmail || employee.email || employee.mobileNumber || 'No contact'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Priority Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority Level</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assignment Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any specific instructions..."
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-600">
            {selectedEmployees.length > 0 && (
              <span className="text-green-600 font-medium">
                ✓ {selectedEmployees.length} employee{selectedEmployees.length > 1 ? 's' : ''} selected
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAssignment}
              disabled={loading || selectedEmployees.length === 0 || loadingData}
              className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
              {loading 
                ? (currentlyAssigned.length > 0 ? 'Reassigning...' : 'Assigning...') 
                : (currentlyAssigned.length > 0 
                    ? `Reassign to ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`
                    : `Assign to ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`
                  )
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedLeadAssignmentModal;
